import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';

import AgreementSection, { type AgreementStatus } from './agreementSection';
import MilestoneTable from './milestoneTable';
import Result from 'components/v2/common/Result';

import { getRetainerTypeLabelKey, sortRetainersOldestFirst } from 'helpers/applicationRetainers';
import useTranslate from 'hooks/useTranslate';
import type { ApplicationRetainer, MilestoneInterface } from 'types';
import { isAgreementSigningAvailable } from 'utils/agreement';

interface CaseRetainersSectionsProps {
    retainers: ApplicationRetainer[];
    /** TWO_STAGE: the visa retainer is issued by the server once documents are preapproved. */
    visaRetainerPending: boolean;
    milestones: MilestoneInterface[];
    isLoadingMilestones: boolean;
    /** Case candidate only; spouses get a view-only action. */
    canSign: boolean;
    paymentStatus?: string | null;
    onOpen: (retainer: ApplicationRetainer) => void;
}

const formatDate = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

/** One section per row of `GET /application/:id/retainers`, oldest first. */
const CaseRetainersSections: React.FC<CaseRetainersSectionsProps> = ({
    retainers,
    visaRetainerPending,
    milestones,
    isLoadingMilestones,
    canSign,
    paymentStatus,
    onOpen,
}) => {
    const { translate } = useTranslate();
    const ordered = sortRetainersOldestFirst(retainers);

    if (ordered.length === 0) {
        return (
            <Result
                title={translate('noRetainersYet')}
                description={translate('noRetainersYetClientDescription')}
            />
        );
    }

    const hasMilestones = milestones.length > 0;

    return (
        <>
            {ordered.map((retainer, index) => {
                const signingAvailable = isAgreementSigningAvailable(retainer);
                const status: AgreementStatus = retainer.signed
                    ? 'signed'
                    : signingAvailable
                      ? 'ready'
                      : 'unavailable';
                const title = translate(getRetainerTypeLabelKey(retainer.agreement_type));
                const signedOn = formatDate(retainer.signed_at);
                const alertMessage = retainer.signed
                    ? signedOn
                        ? `${translate('retainerAlreadySigned')} (${signedOn})`
                        : translate('retainerAlreadySigned')
                    : !signingAvailable
                      ? translate('retainerSigningBlockedClientDescription')
                      : canSign
                        ? translate('retainerReadyToSign')
                        : translate('onlyCandidateCanSign');
                const actionLabel = retainer.signed
                    ? translate('viewSignedCopy')
                    : canSign && signingAvailable
                      ? translate('reviewAndSign')
                      : translate('view');

                return (
                    <AgreementSection
                        key={retainer.agreement_id}
                        title={`${index + 2}. ${title}`}
                        description={translate('caseRetainerClientDescription')}
                        status={status}
                        alertMessage={alertMessage}
                        actionLabel={actionLabel}
                        onAction={() => onOpen(retainer)}
                        showDivider={
                            index < ordered.length - 1 || visaRetainerPending || hasMilestones
                        }
                    />
                );
            })}

            {visaRetainerPending ? (
                <AgreementSection
                    title={`${ordered.length + 2}. ${translate('retainerTypeVisaApplication')}`}
                    description={translate('visaRetainerPendingClientDescription')}
                    status="unavailable"
                    alertMessage={translate('visaRetainerNotReadyYet')}
                    actionLabel={translate('notAvailableYet')}
                    actionDisabled
                    disabledReason={translate('visaRetainerPendingClientDescription')}
                    onAction={() => undefined}
                    showDivider={hasMilestones}
                />
            ) : null}

            <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {translate('paymentMilestones')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {translate('paymentMilestonesDescription')}
                </Typography>
                {isLoadingMilestones ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <MilestoneTable
                        milestones={milestones}
                        applicationPaymentStatus={paymentStatus}
                    />
                )}
            </Box>
        </>
    );
};

export default CaseRetainersSections;
